import { CSSProperties, FC, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  HEAT_STEPS,
  HIGHLIGHT_COLOR,
  MUTED_TEXT_COLOR,
  SURFACE_COLOR,
  WHITE_TEXT_FROM_STEP,
  heatStep,
} from '../../constants/subscriberCohorts';
import {
  CohortCell,
  EMPTY_VALUE,
  HEATMAP_METRICS,
  HeatmapMetric,
  RetentionMode,
  SubscriberCohortsReport,
  averageValue,
  cellValue,
  formatInteger,
  formatMetric,
  heatIntensity,
  maxMetricValue,
  maxOffset,
  monthKey,
} from '../../models/SubscriberCohortsModel';

const METRIC_LABEL_ID: Record<HeatmapMetric, string> = {
  retention: 'metricRetention',
  active: 'metricActive',
  revenue: 'metricRevenue',
  revenueRetention: 'metricRevenueRetention',
  ltv: 'metricLtv',
  ordersPerSubscriber: 'metricOrdersPerSubscriber',
};

const COHORT_COLUMN_WIDTH_PX = 80;
const SIZE_COLUMN_WIDTH_PX = 96;
const VALUE_COLUMN_MIN_WIDTH_PX = 64;
const HIGHLIGHT_BORDER_PX = 3;
const METRIC_SELECT_WIDTH_PX = 280;

const stickyCell = (left: number, width: number): CSSProperties => ({
  position: 'sticky',
  left,
  width,
  minWidth: width,
  background: SURFACE_COLOR,
  zIndex: 1,
});

type Props = {
  report: SubscriberCohortsReport;
  mode: RetentionMode;
  highlighted: string | null;
};

// The classic cohort triangle: a row per cohort, a column per month since it
// started (M0, M1...), shaded on the blue ramp. The month in progress shows in
// muted italics and stays out of the weighted-average row.
const CohortHeatmapTable: FC<Props> = ({ report, mode, highlighted }) => {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string | number>) =>
    intl.formatMessage({ id: `subscriberCohorts.${id}` }, values);
  const [metric, setMetric] = useState<HeatmapMetric>('retention');

  const offsets = Array.from(
    { length: maxOffset(report.cohorts) + 1 },
    (_, offset) => offset
  );
  const maxValue = maxMetricValue(report.cohorts, metric, mode);
  const averageByOffset = new Map(report.average.map((a) => [a.offset, a]));

  function cellTitle(month: string, cell: CohortCell, size: number): string {
    const value = formatMetric(cellValue(cell, metric, mode), metric);
    const parts = [t('cellTitle', { month, offset: cell.offset, value })];
    if (metric === 'retention') {
      parts.push(
        t('cellTitleActive', {
          active: formatInteger(cell.active[mode]),
          size: formatInteger(size),
        })
      );
    }
    if (cell.partial) parts.push(t('partialSuffix'));
    return parts.join(' ');
  }

  function cellStyle(cell: CohortCell): CSSProperties {
    if (cell.partial) {
      return { color: MUTED_TEXT_COLOR, fontStyle: 'italic' };
    }
    const intensity = heatIntensity(cell, metric, mode, maxValue);
    if (intensity === null) return {};
    const step = heatStep(intensity);
    return {
      background: HEAT_STEPS[step],
      color: step >= WHITE_TEXT_FROM_STEP ? SURFACE_COLOR : undefined,
    };
  }

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center gap-4 mb-3">
        <div className="fs-5 fw-bold">{t('table')}</div>
        <select
          className="form-select form-select-sm"
          style={{ width: `${METRIC_SELECT_WIDTH_PX}px` }}
          value={metric}
          aria-label={t('metric')}
          onChange={(e) => setMetric(e.target.value as HeatmapMetric)}
        >
          {HEATMAP_METRICS.map((m) => (
            <option key={m} value={m}>
              {t(METRIC_LABEL_ID[m])}
            </option>
          ))}
        </select>
      </div>
      <div className="table-responsive">
        <table
          className="table table-sm table-borderless fs-8 mb-2"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          <thead>
            <tr className="fw-bold text-muted">
              <th style={stickyCell(0, COHORT_COLUMN_WIDTH_PX)}>
                {t('cohortColumn')}
              </th>
              <th
                className="text-end pe-4"
                style={stickyCell(COHORT_COLUMN_WIDTH_PX, SIZE_COLUMN_WIDTH_PX)}
              >
                {t('sizeColumn')}
              </th>
              {offsets.map((offset) => (
                <th
                  key={offset}
                  className="text-end"
                  style={{ minWidth: VALUE_COLUMN_MIN_WIDTH_PX }}
                >
                  M{offset}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {report.cohorts.map((cohort) => {
              const month = monthKey(cohort.month);
              const isHighlighted = month === highlighted;
              return (
                <tr key={cohort.month}>
                  <th
                    scope="row"
                    className={isHighlighted ? 'fw-bolder' : 'fw-normal'}
                    style={{
                      ...stickyCell(0, COHORT_COLUMN_WIDTH_PX),
                      borderLeft: isHighlighted
                        ? `${HIGHLIGHT_BORDER_PX}px solid ${HIGHLIGHT_COLOR}`
                        : undefined,
                    }}
                  >
                    {month}
                  </th>
                  <td
                    className="text-end pe-4"
                    style={stickyCell(
                      COHORT_COLUMN_WIDTH_PX,
                      SIZE_COLUMN_WIDTH_PX
                    )}
                  >
                    {formatInteger(cohort.size)}
                  </td>
                  {offsets.map((offset) => {
                    const cell = cohort.cells[offset];
                    if (!cell) return <td key={offset} />;
                    return (
                      <td
                        key={offset}
                        className="text-end"
                        style={cellStyle(cell)}
                        title={cellTitle(month, cell, cohort.size)}
                      >
                        {formatMetric(cellValue(cell, metric, mode), metric)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            <tr className="border-top fw-bolder">
              <th
                scope="row"
                colSpan={2}
                style={stickyCell(
                  0,
                  COHORT_COLUMN_WIDTH_PX + SIZE_COLUMN_WIDTH_PX
                )}
              >
                {t('weightedAverage')}
              </th>
              {offsets.map((offset) => {
                const average = averageByOffset.get(offset);
                const value = average
                  ? averageValue(average, metric, mode)
                  : null;
                return (
                  <td key={offset} className="text-end">
                    {value === null ? EMPTY_VALUE : formatMetric(value, metric)}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
      <div className="text-muted fs-8">{t('partialNote')}</div>
    </div>
  );
};

export default CohortHeatmapTable;
